'use client';

/**
 * SSR provider for the search headless-react engine — Coveo's canonical
 * static→hydrate pattern.
 *
 * The server page builds the navigator context, mints the shared anonymous
 * token, seeds the dictionary field context with the shopper currency, and
 * runs the first search; it passes the static state, marshalled navigator
 * context, token, and currency here. The isomorphic StateProvider renders the
 * static (server) state immediately so the server-rendered results paint with
 * no loading flash, then receives the hydrated engine + controllers once
 * client hydration completes. One provider component type for both phases:
 * swapping between the deprecated Static/Hydrated providers would remount the
 * whole result subtree on hydration (they are distinct component types),
 * re-running the entitlements/product-lists providers and their fetches.
 *
 * If hydration fails the static (server) results stay on screen — so a client-
 * init failure can no longer blank the page (the old Safari "black bar" shape).
 * The hydrated state is keyed to the staticState that produced it, so an App
 * Router refresh (e.g. a currency change) that streams a new staticState
 * renders the fresh static results while its re-hydration is pending or
 * failed, instead of a stale previous engine.
 *
 * Hydration runs on a fresh per-hydration definition rather than the module
 * singleton: hydrateStaticState build()s an engine and registers an
 * engine-retaining callback on the definition's token manager that is never
 * released, so repeat hydrations would accumulate engines on the singleton for
 * the life of the SPA session. A per-hydration definition and its engine
 * become garbage together once superseded. The surface's singleton definition
 * (resolved from the `hub` prop, since a definition can't cross the
 * server/client boundary) still owns the generated hooks and StateProvider,
 * which only read React context, and is published to the shared ssr/
 * components through SearchDefinitionContext. URL <->
 * engine-state stays in sync both ways — including browser back/forward — via
 * SearchParameterManager; initial params are seeded server-side from the same
 * query string (SearchSsrPage via toInitialSearchParameters).
 */

import {
  buildSSRSearchParameterSerializer,
  type InferHydratedState,
  type InferStaticState,
  type NavigatorContext,
} from '@coveo/headless-react/ssr';
import { CoveoSearchHub } from '@lib/coveo/coveoConstants';
import {
  claimGestureEntry,
  countGesture,
  isGestureEntryClaimed,
} from '@lib/coveo/historyGesture';
import {
  POPULARITY_CONTEXT_KEY,
  RATING_CONTEXT_KEY,
} from '@lib/coveo/sortCriteriaData';
import * as Sentry from '@sentry/nextjs';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { DEFAULT_CURRENCY } from 'utils';
import { AnalyticsConsentSync } from './AnalyticsConsentSync';
import { createSearchEngineDefinition } from './engineDefinition';
import { logInitialSearch } from './initialSearchLog';
import { migrateLegacyCoveoHash } from './legacyHashParams';
import { addBooleanContext } from './rankingContext';
import {
  getSurfaceEngineDefinition,
  SearchDefinitionProvider,
  useSearchControllers,
  useSearchEngine,
  type SurfaceEngineDefinition,
} from './SearchDefinitionContext';
import { toEngineSearchParameters } from './searchParams';
import { isRelevancySortCriteria } from './sortCriteria';
import { useAppHistoryRouter } from './useAppHistoryRouter';

// Every surface definition comes from the same factory, so one pair of state
// types covers search and listing alike.
type StaticState = InferStaticState<SurfaceEngineDefinition>;
type HydratedState = InferHydratedState<SurfaceEngineDefinition>;

type Hydration = { source: StaticState; state: HydratedState };

/**
 * Whether two URLs carry the same navigation state: same path, fragment, and
 * query params regardless of encoding and ordering. The serializer's output
 * (URLSearchParams form-encoding: '+' for spaces, Coveo params appended last)
 * rarely string-matches an inbound URL ('%20' for spaces, original order), and
 * writing history over such a cosmetic difference re-renders every
 * useSearchParams consumer via Next's patched history methods and rewrites the
 * URL the user shared.
 */
function isSameUrlState(a: string, b: string): boolean {
  const urlA = new URL(a, window.location.origin);
  const urlB = new URL(b, window.location.origin);
  if (urlA.pathname !== urlB.pathname || urlA.hash !== urlB.hash) {
    return false;
  }
  const sortedEntries = (url: URL) =>
    Array.from(url.searchParams.entries())
      .map(([key, value]) => `${key}=${value}`)
      .sort();
  const entriesA = sortedEntries(urlA);
  const entriesB = sortedEntries(urlB);
  return (
    entriesA.length === entriesB.length &&
    entriesA.every((entry, index) => entry === entriesB[index])
  );
}

/**
 * Counts user gestures into the shared historyGesture tally, for attributing
 * a URL write to the interaction behind it. Counted from the capture phase, so
 * the tally is already current by the time the handler that changes engine
 * state — or writes a non-Coveo param directly — runs.
 *
 * `click` is counted alongside the input events because an assistive
 * activation (voice control, some screen-reader modes) dispatches a bare
 * `click` with no preceding `pointerdown`/`keydown`, and an uncounted gesture
 * would `replace` the entry the previous filter state lives in instead of
 * pushing a new one. A pointer or keyboard activation counts twice — the
 * events that precede its `click` — which is harmless: only the change in the
 * tally attributes the write, never its magnitude.
 */
const GESTURE_EVENTS = ['pointerdown', 'keydown', 'click'] as const;

function useGestureCount() {
  useEffect(() => {
    GESTURE_EVENTS.forEach((type) =>
      window.addEventListener(type, countGesture, true)
    );
    return () => {
      GESTURE_EVENTS.forEach((type) =>
        window.removeEventListener(type, countGesture, true)
      );
    };
  }, []);
}

/**
 * Two-way sync between the URL query string (SSR-friendly) and the engine's
 * search parameters — Coveo's canonical SearchParameterManager pattern.
 *
 * - URL → state: on every URL change (including browser back/forward, via the
 *   popstate-driven `useAppHistoryRouter`), synchronize the engine from the URL.
 *   `methods` is undefined until the hydrated engine is in the StateProvider,
 *   so this no-ops during the static phase.
 * - state → URL: when the parameters change once interactive, write them back,
 *   with one history entry per user interaction (so back/forward steps through
 *   filter/sort/page changes). The static phase never writes: the server
 *   rendered from this same URL, and a static-phase serialize would wipe any
 *   Coveo param the server didn't seed — most importantly the just-migrated
 *   legacy-hash state below — before the hydrated engine gets to consume it.
 *
 * Rendered inside the StateProvider so it spans both phases. Non-Coveo query
 * params are preserved by the serializer.
 */
function SearchParameterManager({
  defaultPageSize,
  isListing,
}: {
  defaultPageSize: number;
  isListing: boolean;
}) {
  // [TEMP: legacy-search back-compat] One-shot migration of a v1-style Coveo
  // hash (e.g. /search#q=trees&cf-ec_category=3d) into the query string, so old
  // deep links restore state under SSR: the hydration synchronize below then
  // seeds the engine from the migrated query string. During render — not in an
  // effect — so it lands before useAppHistoryRouter captures the URL (render
  // side effect precedent: CsrFallback). Idempotent under StrictMode's double
  // render. Remove with legacy search — see legacyHashParams.
  if (typeof window !== 'undefined') {
    migrateLegacyCoveoHash();
  }

  const historyRouter = useAppHistoryRouter();
  useGestureCount();
  const engine = useSearchEngine();
  const controllers = useSearchControllers();
  const { state, methods } = controllers.useSearchParameterManager();
  const { methods: contextMethods } = controllers.useContext();

  // URL → engine state (fires on back/forward as popstate updates the url, and
  // again when a re-hydration delivers a new `methods`). Reads the LIVE URL:
  // historyRouter.url only refreshes on popstate, so after any push it is
  // stale, and a re-hydration (e.g. a currency change streaming a new
  // staticState) re-running this effect against it would rewind the engine —
  // and the state→URL effect would then push the stale URL over the correct
  // one. Runs through the same allowlist as server-side seeding so a crafted
  // query param can't reach the client engine either.
  useEffect(() => {
    if (!methods) {
      return;
    }
    const liveParams = new URL(window.location.href).searchParams;
    // The engine's own `defaultNumberOfResults`, not the server-resolved prop:
    // restoreSearchParameters falls back to it for a URL without
    // `numberOfResults`, and a page-size change mid-session moves it (the
    // controls re-register it). Aligning against the stale initial size would
    // rewind a within-session history entry to page 1.
    const effectivePageSize =
      engine?.state.pagination?.defaultNumberOfResults ?? defaultPageSize;
    const parameters = toEngineSearchParameters(liveParams, effectivePageSize);
    // The ranking flags live in the engine context, not the URL, so restoring
    // the relevancy sort after a field sort cleared them would otherwise run
    // the listing on plain relevance under a Popularity label. Set before
    // synchronize, whose search reads the context.
    const contextValues = engine?.state.context?.contextValues;
    if (
      isListing &&
      isRelevancySortCriteria(parameters.sortCriteria) &&
      !contextValues?.[POPULARITY_CONTEXT_KEY] &&
      !contextValues?.[RATING_CONTEXT_KEY]
    ) {
      addBooleanContext(contextMethods, POPULARITY_CONTEXT_KEY, true);
    }
    methods.synchronize(parameters);
    // historyRouter.url is only the popstate re-run trigger, not a data source.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyRouter.url?.searchParams, methods, engine, defaultPageSize]);

  // Corrected URL derived from engine state. Intentionally keyed only on
  // state.parameters: on back/forward the URL→state effect updates state, which
  // recomputes this to match the restored URL (so the state→URL effect no-ops
  // rather than fighting the navigation).
  const correctedUrl = useMemo(() => {
    if (!historyRouter.url) {
      return null;
    }
    const { serialize } = buildSSRSearchParameterSerializer();
    // Serialize onto the LIVE URL, not the router's cached `url` (which only
    // refreshes on popstate). This preserves non-Coveo params written via a bare
    // history.pushState — e.g. OwnershipFacet's `hideOwnership` — instead of
    // clobbering them on the next facet/sort/page write.
    return serialize(state.parameters, new URL(window.location.href));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.parameters]);

  // engine state → URL, once interactive (see the component doc for why the
  // static phase must not write). Skipped on the commit where `methods`
  // (re)appears: the URL→state effect above has just synchronized, but this
  // render's correctedUrl still reflects the PRE-synchronize state — writing it
  // would push that stale state over the live URL (with a migrated legacy hash,
  // a browse-all URL over the deep link). The synchronize's own state change
  // re-runs this effect with a fresh correctedUrl.
  //
  // One entry per interaction, not per state change: a controller method that
  // dispatches more than once — Headless' `toggleSingleSelect` (the category
  // tree's replace-the-selection path) deselects, then selects — walks the
  // engine through an intermediate state, and React commits that state on its
  // own often enough for it to reach this effect. Pushing on every commit
  // leaves the intermediate (deselected) URL behind as an entry the user never
  // visited, so Back lands on unfiltered results instead of the previous
  // selection. The first write of a gesture creates the entry and the rest of
  // that gesture refines it in place; a write with no gesture behind it (a
  // re-synchronize, a migrated legacy hash) refines too, so deep links don't
  // accumulate entries the user never navigated to. The tally is shared
  // (historyGesture) rather than local because the same gesture can also be
  // behind a bare non-Coveo param write — the drawer's Clear all clears
  // `hideOwnership` as well as every facet — and whichever of the two writes
  // first owns the gesture's entry.
  const previousMethodsRef = useRef<typeof methods>();
  useEffect(() => {
    const isHydrationCommit = methods !== previousMethodsRef.current;
    previousMethodsRef.current = methods;
    if (
      !methods ||
      !correctedUrl ||
      isHydrationCommit ||
      isSameUrlState(window.location.href, correctedUrl)
    ) {
      return;
    }

    if (isGestureEntryClaimed()) {
      historyRouter.replace(correctedUrl);
      return;
    }
    claimGestureEntry();
    historyRouter.push(correctedUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correctedUrl, methods]);

  return null;
}

type SearchEngineProviderProps = {
  staticState: StaticState;
  navigatorContext: NavigatorContext;
  accessToken: string;
  currency: string;
  /** Route locale — drives Coveo linguistic processing on client searches. */
  locale: string;
  /**
   * The server-resolved default page size (saved-preference cookie, else the
   * flag-driven default). The hydration definition must be created with the
   * same value the server definition used, or the hydrated engine's pagination
   * default diverges from the replayed server search.
   */
  defaultPageSize: number;
  /**
   * The surface's Coveo search hub. A serializable stand-in for the engine
   * definition itself, which carries functions and so cannot cross the
   * server/client boundary: the client resolves the matching singleton (whose
   * React context the generated hooks read) from it.
   */
  hub: CoveoSearchHub;
  children: ReactNode;
};

export function SearchEngineProvider({
  staticState,
  navigatorContext,
  accessToken,
  currency,
  locale,
  defaultPageSize,
  hub,
  children,
}: SearchEngineProviderProps) {
  const [hydration, setHydration] = useState<Hydration | null>(null);
  // Fixed for the lifetime of this tree: a route renders one surface, and the
  // other one arrives as a fresh mount (see SearchDefinitionContext).
  const definition = getSurfaceEngineDefinition(hub);

  useEffect(() => {
    logInitialSearch(staticState.searchAction, hub);
  }, [staticState, hub]);

  useEffect(() => {
    let cancelled = false;
    let hydratedEngine: HydratedState['engine'] | undefined;

    // The route locale must reach the client engine too: every client-initiated
    // search (facet clicks, back/forward URL synchronize) would otherwise run
    // with the definition's DEFAULT_LOCALE linguistic processing, diverging
    // from the server's locale-correct first search.
    const hydrationDefinition = createSearchEngineDefinition(
      hub,
      locale,
      defaultPageSize,
      () => hydratedEngine?.state.pagination?.numberOfResults
    );
    // Navigator context + (shared anonymous) token must be set before hydrating
    // the client engine; the server used equivalents for fetchStaticState.
    hydrationDefinition.setNavigatorContextProvider(() => navigatorContext);
    // An empty token would fail the engine's configuration validation and
    // permanently abort hydration; with the placeholder left in place the
    // first client search 401s and renewAccessToken mints a fresh anonymous
    // token instead.
    if (accessToken) {
      hydrationDefinition.setAccessToken(accessToken);
    }

    const { context, searchParameterManager } = staticState.controllers;
    hydrationDefinition
      .hydrateStaticState({
        searchAction: staticState.searchAction,
        controllers: {
          context: { initialState: context.state },
          searchParameterManager: {
            initialState: searchParameterManager.state,
          },
        },
      })
      .then((h) => {
        if (cancelled) return;
        hydratedEngine = h.engine;
        // Re-seed the shopper currency on the hydrated engine so later searches
        // keep querying in it, and so components reading the display currency
        // off the dictionary context state stay in sync with the replayed
        // (already converted) server results. hydrateStaticState controller
        // props can't carry a dictionary field context, and `set` only
        // dispatches state — it executes no query. USD is the index default.
        if (currency !== DEFAULT_CURRENCY) {
          h.controllers.dictionaryFieldContext.set({
            ec_price: currency,
            ec_price_filter: currency,
          });
        }
        setHydration({ source: staticState, state: h });
      })
      .catch((error) => {
        // Static results remain visible; only interactivity is lost.
        Sentry.withScope((scope) => {
          scope.setTags({
            'coveo.hub': hub,
            'coveo.step': 'hydrate',
          });
          Sentry.captureException(error);
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    staticState,
    navigatorContext,
    accessToken,
    currency,
    locale,
    defaultPageSize,
    hub,
  ]);

  const hydrated =
    hydration && hydration.source === staticState ? hydration.state : null;

  return (
    <SearchDefinitionProvider definition={definition}>
      <definition.StateProvider
        engine={hydrated?.engine}
        controllers={hydrated ? hydrated.controllers : staticState.controllers}
      >
        <SearchParameterManager
          defaultPageSize={defaultPageSize}
          isListing={hub === CoveoSearchHub.LISTING}
        />
        <AnalyticsConsentSync />
        {children}
      </definition.StateProvider>
    </SearchDefinitionProvider>
  );
}
